"use client"

// AuthContext.js
import { createContext, useContext, useEffect, useState } from "react"
import { auth } from "../firebase"
import {
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    signInWithPopup,
    createUserWithEmailAndPassword,
    deleteUser,
    reauthenticateWithCredential,
    EmailAuthProvider,
} from "firebase/auth"
import { glogin, login, registerUser, deleteSelf } from "../services/usersApi"
import { saveJwtToken, removeJwtToken, removeUserData, saveUserData, getUserData, setgLogin } from "./auth"
import { toast } from "react-toastify"

const AuthContext = createContext()

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null)
    const [loading, setLoading] = useState(true)
    const [hasJustSignedOut, setHasJustSignedOut] = useState(false)
    const [justLoggedIn, setJustLoggedIn] = useState(false)
    // Agrega un estado para userData
    const [userData, setUserData] = useState(getUserData())

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                try {
                    const token = await firebaseUser.getIdToken()
                    setUser(firebaseUser.email)
                    saveJwtToken(token)
                    // Si no existe userData en localStorage, o si deseas refrescarlo,
                    // haz la llamada al backend y actualiza tanto en localStorage como en estado.
                    if (getUserData() === null) {
                        console.log("User data not found, fetching from backend")
                        const data = await login()
                        saveUserData(data)
                        setUserData(data)
                    } else {
                        // Si ya existe, asegúrate de tenerlo en el estado
                        setUserData(getUserData())
                    }
                    setHasJustSignedOut(false)
                } catch (error) {
                    console.error("Error fetching user data:", error)
                }
            } else {
                removeJwtToken()
                removeUserData()
                setUser(null)
                setUserData(null)
            }
            setLoading(false)
        })

        return () => unsubscribe()
    }, [])

    const handleSignOut = async () => {
        try {
            await signOut(auth)
            removeJwtToken()
            removeUserData()
            setUser(null)
            setUserData(null)
            setHasJustSignedOut(true)
            setJustLoggedIn(false)
        } catch (error) {
            console.error("Error signing out:", error)
        }
    }

    const handleLogin = async (email, password) => {
        const response = await signInWithEmailAndPassword(auth, email, password)
        const token = await response.user.getIdToken()
        saveJwtToken(token)
        const data = await login()
        saveUserData(data)
        setUser(response.user.email)
        setUserData(data)
        setJustLoggedIn(true)
        return response.user
    }

    const handleGoogleSignIn = async (provider) => {
        try {
            const response = await signInWithPopup(auth, provider)
            const token = await response.user.getIdToken()
            saveJwtToken(token)
            const userPayload = {
                email: response.user.email,
                name: response.user.displayName,
            }
            setgLogin(true)
            const data = await glogin(userPayload)
            saveUserData(data)
            setUser(response.user.email)
            setUserData(data)
            setJustLoggedIn(true)
            // Verificar si necesita completar localidad o aceptar términos
            return !data.location || !data.termsAccepted || data.termsAccepted === "false" || data.termsAccepted === null
        } catch (error) {
            console.error("Error en Google Sign-In:", error)
            toast.error("Ocurrió un error al iniciar sesión con Google.")
            throw error
        }
    }

    const handleSignUp = async (formData) => {
        const { email, password } = formData
        try {
            const response = await createUserWithEmailAndPassword(auth, email, password)
            const token = await response.user.getIdToken()
            saveJwtToken(token)

            // Registrar usuario en el backend
            await registerUser(formData)

            // Obtener datos completos del usuario desde el backend
            const completeUserData = await login()

            saveUserData(completeUserData)
            setUser(response.user.email)
            setUserData(completeUserData)
            toast.success("¡Registro exitoso!")

            return completeUserData
        } catch (error) {
            if (error.code === "auth/email-already-in-use") {
                toast.error("El correo electrónico ya está en uso.")
            } else {
                console.error("Error durante el registro:", error)
                toast.error("Error al registrar el usuario. Inténtalo de nuevo.")
            }
            throw error
        }
    }

    const handlePasswordReset = async (email) => {
        if (!email) throw new Error("Email is required")
        return await sendPasswordResetEmail(auth, email)
    }

    const handleDeleteAccount = async () => {
        try {
            const currentUser = auth.currentUser

            if (!currentUser) {
                throw new Error("No user is currently logged in")
            }

            // Primero verificar si es un usuario de Google
            const isGoogleUserAccount = isGoogleUser()

            // Para usuarios de Google, siempre requerir reautenticación
            // Para usuarios de email/password, verificar la antigüedad de la sesión
            if (isGoogleUserAccount) {
                return {
                    success: false,
                    requiresRecentLogin: true,
                    isGoogleUser: true,
                    message: "Por seguridad, debes volver a autenticarte con Google antes de eliminar tu cuenta.",
                }
            }

            // Para usuarios de email/password, intentar eliminar directamente
            // Firebase solo lanzará auth/requires-recent-login si realmente es necesario
            try {
                // Eliminar del backend primero
                await deleteSelf()

                // Luego eliminar de Firebase Authentication
                await deleteUser(currentUser)

                // Clean up local data
                removeJwtToken()
                removeUserData()
                setUser(null)
                setUserData(null)
                setHasJustSignedOut(true)

                return { success: true }
            } catch (deleteError) {
                if (deleteError.code === "auth/requires-recent-login") {
                    return {
                        success: false,
                        requiresRecentLogin: true,
                        isGoogleUser: false,
                        message: "Por seguridad, debes volver a iniciar sesión antes de eliminar tu cuenta.",
                    }
                }
                throw deleteError
            }
        } catch (error) {
            console.error("Error deleting account:", error)
            throw error
        }
    }

    // Add this function to check if a user is a Google-authenticated user
    const isGoogleUser = () => {
        const currentUser = auth.currentUser
        if (!currentUser) return false

        // Check if the user has a Google provider
        const providers = currentUser.providerData || []
        return providers.some((provider) => provider.providerId === "google.com")
    }

    // Update the handleReauthenticate function to handle Google users
    const handleReauthenticate = async (password, googleProvider = null) => {
        try {
            const currentUser = auth.currentUser
            if (!currentUser) {
                throw new Error("No user is currently logged in")
            }

            // If user is a Google user and a provider is passed
            if (isGoogleUser() && googleProvider) {
                // Re-authenticate with Google
                const result = await signInWithPopup(auth, googleProvider)
                return { success: true }
            } else if (!isGoogleUser() && password) {
                // Re-authenticate with password
                const credential = EmailAuthProvider.credential(currentUser.email, password)
                await reauthenticateWithCredential(currentUser, credential)
                return { success: true }
            } else {
                throw new Error("Invalid authentication method")
            }
        } catch (error) {
            console.error("Error reauthenticating:", error)

            if (error.code === "auth/wrong-password") {
                return {
                    success: false,
                    message: "Contraseña incorrecta. Por favor, intenta nuevamente.",
                }
            }

            return {
                success: false,
                message: "Error al autenticar. Por favor, intenta nuevamente.",
            }
        }
    }

    return (
        <AuthContext.Provider
            value={{
                user,
                setUser,
                loading,
                setLoading,
                handleSignOut,
                handleLogin,
                handleGoogleSignIn,
                handlePasswordReset,
                handleSignUp,
                handleDeleteAccount,
                handleReauthenticate,
                isGoogleUser,
                hasJustSignedOut,
                setHasJustSignedOut,
                justLoggedIn,
                setJustLoggedIn,
                // Expones userData y su setter
                userData,
                setUserData,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext)
